// ─────────────────────────────────────────────────────────────────────────────
// V2Sales — the Sales Team group in the v2 design: Salespeople, Salesperson
// profile, Commissions. Endpoints are v1's:
//   /api/salesperson/all | /:id | /create (POST) | /update/:id (PUT) | /delete/:id (DELETE)
//   /api/invoices/salesperson/:id → { invoices, summary }
//   /api/commission/summary | /all | /pending | /salesperson/:id | /markpaid-bulk (PUT { commissionIds, notes })
// Status rules (server): blacklisted salespeople cannot be put on new invoices;
// "flagged" is advisory only.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, inr, useMedia } from "./primitives";
import { Field, PageHead, Stat } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const ini = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const apiErr = (d, fallback) => d?.error || d?.errors?.[0]?.msg || fallback;
const STATUS = {
  active: { label: "Active", tone: "good", glyph: "check", desc: "Can be assigned to new invoices." },
  flagged: { label: "Flagged", tone: "warn", glyph: "alert", desc: "Still assignable — a marker for your team to review." },
  blacklisted: { label: "Blacklisted", tone: "bad", glyph: "close", desc: "Blocked — cannot be put on new invoices." },
};
const rateText = (s) => (s.commissionType === "percentage" ? `${s.commissionRate}% commission` : s.commissionType === "flat" ? `${inr(s.commissionRate)} per invoice` : "No commission");
const StatusPill = ({ s }) => { const c = STATUS[s] || STATUS.active; return <span className={`v2-status v2-status--${c.tone}`}><Glyph name={c.glyph} size={12} stroke={2.4} /> {c.label}</span>; };

function useSummary() {
  const [sum, setSum] = useState(null);
  const load = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/api/commission/summary`, { headers: auth() }); setSum(r.ok ? await r.json() : {}); } catch { setSum({}); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return [sum, load];
}

// ══════════════════════════════════════════════════════════════════════════════
// SALESPEOPLE
// ══════════════════════════════════════════════════════════════════════════════
export function V2Salespeople({ showAlert }) {
  usePageTitle("Salespeople");
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("sales", "write");
  const [people, setPeople] = useState(null);
  const [sum] = useSummary();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const newBtn = useRef(null);

  const load = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/api/salesperson/all`, { headers: auth() }); const d = await r.json(); setPeople(Array.isArray(d) ? d : d?.salespeople || []); }
    catch { setPeople([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = people || [];
  const n = (s) => all.filter((p) => (p.status || "active") === s).length;
  const shown = all.filter((p) => (filter === "all" || (p.status || "active") === filter)
    && (!q.trim() || `${p.name} ${p.employeeId} ${p.email} ${p.phone}`.toLowerCase().includes(q.trim().toLowerCase())))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="v2p">
      <PageHead eyebrow="Sales team" title="Salespeople" sub="Who sells for you, how they earn, and whether they can bill."
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setEditing("new")}><Glyph name="plus" size={17} stroke={2.2} /> Add salesperson</button>} />
      <div className="v2k-stats">
        <Stat label="Team" value={people ? all.length : "—"} note={people ? `${n("active")} active` : ""} />
        <Stat label="Needs review" value={people ? n("flagged") + n("blacklisted") : "—"} note={people ? `${n("flagged")} flagged · ${n("blacklisted")} blacklisted` : ""} />
        <Stat label="Commission earned" value={sum ? inr(sum.totalCommission || 0) : "—"} note={sum ? `${sum.totalCount || 0} invoices` : ""} />
        <Stat label="Pending payout" value={sum ? inr(sum.pendingCommission || 0) : "—"} note={sum?.pendingCount ? <button type="button" className="v2k-link" onClick={() => navigate("/dashboard/sales/commissions")}>Pay {sum.pendingCount} →</button> : "All settled"} />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "flagged", label: "Flagged" }, { value: "blacklisted", label: "Blacklisted" }]} />
        <div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, employee ID, email, phone" aria-label="Search salespeople" /></div>
      </div>
      <section className="v2-card v2i-card">
        {!people ? <div className="v2-list">{[0, 1, 2].map((i) => <Skel key={i} h={60} r={16} />)}</div>
          : shown.length === 0 ? <div className="v2c-empty"><Duo name="people" size={48} /><strong>{all.length ? "No one here" : "No salespeople yet"}</strong><span>Add salespeople to credit invoices to them and track commission.</span>{!all.length && canWrite && <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => setEditing("new")}>Add salesperson</button>}</div>
          : <div className="v2i-rows">{shown.map((p) => (
            <div key={p._id} className="v2cn-row">
              <button type="button" className="v2cn-open" onClick={() => navigate(`/dashboard/sales/salesperson/${p._id}`)}>
                <span className={`v2-avatar v2-avatar--client${p.status === "blacklisted" ? " is-bad" : ""}`} style={{ width: 40, height: 40, fontSize: 14 }}>{ini(p.name)}</span>
                <span className="v2-row-main"><span className="v2-row-title">{p.name}</span><span className="v2-row-sub">{p.employeeId} · {p.email}</span></span>
                <span className="v2sl-rate">{rateText(p)}</span>
                <span className="v2cn-end"><StatusPill s={p.status} /></span>
              </button>
              {canWrite && <MenuButton className="v2-icon-btn v2i-more" label={`Options for ${p.name}`} width={220} items={[
                { label: "Open profile", glyph: "people", onSelect: () => navigate(`/dashboard/sales/salesperson/${p._id}`) },
                { label: "Edit", glyph: "note", onSelect: () => setEditing(p) },
                "divider", { label: "Delete", glyph: "close", danger: true, onSelect: () => setRemoving(p) }]} />}
            </div>))}</div>}
      </section>
      <SalespersonEditor value={editing} originRef={newBtn} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); showAlert?.(m, "success"); load(); }} />
      <Sheet open={!!removing} onClose={() => setRemoving(null)} width={440} title={`Delete ${removing?.name || ""}?`}
        footer={<><button type="button" className="v2-btn" onClick={() => setRemoving(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={async () => {
            try { const r = await fetch(`${API_BASE}/api/salesperson/delete/${removing._id}`, { method: "DELETE", headers: auth() }); const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(apiErr(d, "Couldn't delete")); showAlert?.("Salesperson deleted", "success"); setRemoving(null); load(); }
            catch (e) { setRemoving((x) => ({ ...x, error: e.message })); }
          }}>Delete</button></>}>
        <p className="v2c-remove-text">To stop them billing without losing their history, <b>blacklist</b> them from their profile instead.</p>
        {removing?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {removing.error}</div>}
      </Sheet>
    </div>
  );
}

const EMPTY = { name: "", employeeId: "", email: "", phone: "", commissionType: "percentage", commissionRate: "" };
function SalespersonEditor({ value, originRef, onClose, onSaved }) {
  const edit = value && value !== "new" ? value : null;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!value) return; setErr("");
    setF(edit ? { name: edit.name, employeeId: edit.employeeId, email: edit.email, phone: String(edit.phone ?? ""), commissionType: edit.commissionType || "percentage", commissionRate: edit.commissionRate ?? "" } : EMPTY);
  }, [value]); // eslint-disable-line
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault(); setErr("");
    const phone = f.phone.replace(/\D/g, "");
    if (f.name.trim().length < 3) { setErr("Name needs at least 3 characters."); return; }
    if (!edit && !f.employeeId.trim()) { setErr("Employee ID is required."); return; }
    if (phone.length < 10) { setErr("Enter a 10-digit phone number."); return; }
    const rate = f.commissionType === "none" ? 0 : parseFloat(f.commissionRate) || 0;
    if (f.commissionType === "percentage" && (rate < 0 || rate > 100)) { setErr("Percentage must be between 0 and 100."); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim(), email: f.email.trim(), phone, commissionType: f.commissionType, commissionRate: rate };
      if (!edit) body.employeeId = f.employeeId.trim();
      const r = await fetch(edit ? `${API_BASE}/api/salesperson/update/${edit._id}` : `${API_BASE}/api/salesperson/create`, { method: edit ? "PUT" : "POST", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(apiErr(d, "Couldn't save"));
      onSaved(edit ? "Salesperson updated" : `${body.name} added`);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!value} onClose={onClose} originRef={edit ? undefined : originRef} width={540} title={edit ? "Edit salesperson" : "Add salesperson"}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="submit" form="v2sl-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Add salesperson"}</button></>}>
      <form id="v2sl-form" className="v2-form" onSubmit={save}>
        <div className="v2-form-row">
          <Field label="Full name"><input className="v2-input" autoFocus value={f.name} onChange={set("name")} /></Field>
          <Field label="Employee ID" hint={edit ? "Can't be changed" : undefined}><input className="v2-input" value={f.employeeId} onChange={set("employeeId")} disabled={!!edit} placeholder="e.g. EMP-014" /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Email"><input className="v2-input" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label="Phone"><input className="v2-input" type="tel" inputMode="numeric" value={f.phone} onChange={set("phone")} /></Field>
        </div>
        <Field label="Commission"><Segmented ariaLabel="Commission type" value={f.commissionType} onChange={(v) => setF((p) => ({ ...p, commissionType: v }))} options={[{ value: "percentage", label: "% of sale" }, { value: "flat", label: "Flat per invoice" }, { value: "none", label: "None" }]} /></Field>
        {f.commissionType !== "none" && (
          <Field label={f.commissionType === "percentage" ? "Rate" : "Amount per invoice"}>
            <span className="v2-input-wrap">
              <span className="v2-input-prefix" style={{ fontSize: 16 }}>{f.commissionType === "percentage" ? "%" : "₹"}</span>
              <input className="v2-input" style={{ paddingLeft: 30 }} type="number" min="0" step="0.01" value={f.commissionRate} onChange={set("commissionRate")} />
            </span>
          </Field>)}
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SALESPERSON PROFILE
// ══════════════════════════════════════════════════════════════════════════════
export function V2SalespersonProfile({ showAlert }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("sales", "write");
  const isNarrow = useMedia("(max-width: 760px)");
  const [sp, setSp] = useState(null);
  const [inv, setInv] = useState(null);
  const [com, setCom] = useState(null);
  const [tab, setTab] = useState("invoices");
  const [editing, setEditing] = useState(null);
  const [statusTo, setStatusTo] = useState(null);
  usePageTitle(sp?.name || "Salesperson");

  const load = useCallback(async () => {
    const get = (u) => fetch(`${API_BASE}${u}`, { headers: auth() }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
    get(`/api/salesperson/${id}`).then((d) => setSp(d?.salesperson || d)).catch(() => setSp(false));
    get(`/api/invoices/salesperson/${id}`).then((d) => setInv(d || { invoices: [], summary: {} })).catch(() => setInv({ invoices: [], summary: {} }));
    get(`/api/commission/salesperson/${id}`).then((d) => setCom(Array.isArray(d) ? d : [])).catch(() => setCom([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/salesperson/update/${id}`, { method: "PUT", headers: json(), body: JSON.stringify({ status: statusTo }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(apiErr(d, "Couldn't update status"));
      showAlert?.(`Status set to ${STATUS[statusTo].label}`, "success"); setStatusTo(null); load();
    } catch (e) { setStatusTo(null); showAlert?.(e.message, "danger"); }
  };

  if (sp === false) return <div className="v2p"><section className="v2-card"><div className="v2c-empty"><Duo name="people" size={48} /><strong>Salesperson not found</strong><button type="button" className="v2-btn v2-btn--sm" onClick={() => navigate("/dashboard/sales/salespeople")}>Back to salespeople</button></div></section></div>;
  const invoices = inv?.invoices || [];
  const pend = (com || []).filter((c) => c.paymentStatus === "pending");
  const earned = (com || []).reduce((a, c) => a + (c.commissionAmount || 0), 0);
  const st = sp?.status || "active";

  return (
    <div className="v2p v2l">
      <button type="button" className="v2l-back" onClick={() => navigate("/dashboard/sales/salespeople")}><Glyph name="chevronLeft" size={18} stroke={2} /> Salespeople</button>
      <section className="v2-card v2sl-hero">
        {!sp ? <Skel h={88} r={20} /> : <>
          <span className={`v2-avatar v2-avatar--client${st === "blacklisted" ? " is-bad" : ""}`} style={{ width: 64, height: 64, fontSize: 22 }}>{ini(sp.name)}</span>
          <div className="v2l-hero-text">
            <h2>{sp.name}</h2>
            <span className="v2-row-sub">{sp.employeeId} · {rateText(sp)}</span>
            <span className="v2sl-contact">
              {sp.email && <a href={`mailto:${sp.email}`}><Glyph name="inbox" size={13} /> {sp.email}</a>}
              {sp.phone && <a href={`tel:${sp.phone}`}><Glyph name="phone" size={13} /> {sp.phone}</a>}
            </span>
          </div>
          <div className="v2l-hero-actions">
            {canWrite ? <MenuButton className="v2-btn v2-btn--sm v2sl-status-btn" label="Change status" width={260} header="Billing status"
              items={Object.entries(STATUS).map(([k, c]) => ({ label: c.label, glyph: c.glyph, active: k === st, onSelect: () => k !== st && setStatusTo(k) }))}>
              <StatusPill s={st} /><Glyph name="chevronDown" size={14} stroke={2} /></MenuButton>
              : <StatusPill s={st} />}
            {canWrite && <button type="button" className="v2-btn v2-btn--sm" onClick={() => setEditing(sp)}><Glyph name="note" size={15} /> Edit</button>}
          </div>
        </>}
      </section>
      {st !== "active" && sp && <div className={`v2-banner v2-banner--${STATUS[st].tone}`}><Glyph name="alert" size={16} /> {STATUS[st].desc}</div>}
      <div className="v2k-stats">
        <Stat label="Total sales" value={inv ? inr(inv.summary?.totalSales || 0) : "—"} note={inv ? `${inv.summary?.totalInvoices || 0} active invoices` : ""} />
        <Stat label="Average invoice" value={inv && invoices.length ? inr((inv.summary?.totalSales || 0) / invoices.length) : "—"} note="Per invoice" />
        <Stat label="Commission earned" value={com ? inr(earned) : "—"} note={com ? `${com.length} records` : ""} />
        <Stat label="Pending payout" value={com ? inr(pend.reduce((a, c) => a + c.commissionAmount, 0)) : "—"} note={com ? (pend.length ? `${pend.length} unpaid` : "All paid") : ""} />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="View" value={tab} onChange={setTab} options={[{ value: "invoices", label: `Invoices${inv ? ` · ${invoices.length}` : ""}` }, { value: "commissions", label: `Commissions${com ? ` · ${com.length}` : ""}` }]} />
      </div>
      <section className="v2-card v2l-statement">
        {tab === "invoices" ? (
          !inv ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2].map((i) => <Skel key={i} h={52} r={14} />)}</div>
            : invoices.length === 0 ? <div className="v2c-empty"><Duo name="invoice" size={44} /><span>No invoices credited to {sp?.name || "them"} yet.</span></div>
            : invoices.map((v) => (
              <div key={v._id} className="v2l-entry">
                <span className="v2pub-day"><strong>{new Date(v.invoiceDate).getDate()}</strong><span>{new Date(v.invoiceDate).toLocaleDateString("en-IN", { month: "short" })}</span></span>
                <span className="v2l-entry-main"><span className="v2l-entry-desc">{v.invoiceNumber}</span><span className="v2l-entry-meta">{v.clientId?.clientName || "—"}{isNarrow ? "" : ` · ${fmtDate(v.invoiceDate)}`}</span></span>
                <span className="v2l-entry-amts"><span className="v2l-amt">{inr(v.grandTotal, { decimals: 2 })}</span></span>
              </div>))
        ) : (
          !com ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2].map((i) => <Skel key={i} h={52} r={14} />)}</div>
            : com.length === 0 ? <div className="v2c-empty"><Duo name="cash" size={44} /><span>No commission recorded yet.</span></div>
            : com.map((c) => (
              <div key={c._id} className="v2l-entry">
                <span className="v2pub-day"><strong>{new Date(c.createdAt).getDate()}</strong><span>{new Date(c.createdAt).toLocaleDateString("en-IN", { month: "short" })}</span></span>
                <span className="v2l-entry-main"><span className="v2l-entry-desc">{c.invoiceNumber || c.invoice?.invoiceNumber}</span><span className="v2l-entry-meta">on {inr(c.saleAmount)} · {c.commissionType === "percentage" ? `${c.commissionRate}%` : c.commissionType === "flat" ? "flat" : "none"}</span></span>
                <span className="v2l-entry-amts"><span className="v2l-amt">{inr(c.commissionAmount, { decimals: 2 })}</span>
                  {c.paymentStatus === "paid" ? <span className="v2-status v2-status--good">Paid {fmtDate(c.paidDate)}</span> : <span className="v2-status v2-status--warn">Pending</span>}</span>
              </div>))
        )}
      </section>
      {pend.length > 0 && canWrite && <button type="button" className="v2-btn v2sl-paylink" onClick={() => navigate("/dashboard/sales/commissions", { state: { salespersonId: id } })}><Glyph name="cash" size={15} /> Pay {pend.length} pending in Commissions</button>}
      <SalespersonEditor value={editing} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); showAlert?.(m, "success"); load(); }} />
      <Sheet open={!!statusTo} onClose={() => setStatusTo(null)} width={420} title={statusTo ? `Mark ${sp?.name} ${STATUS[statusTo].label.toLowerCase()}?` : ""}
        footer={<><button type="button" className="v2-btn" onClick={() => setStatusTo(null)}>Cancel</button>
          <button type="button" className={`v2-btn ${statusTo === "blacklisted" ? "v2c-btn-danger" : "v2-btn--primary"}`} onClick={setStatus}>{statusTo ? `Set ${STATUS[statusTo].label}` : ""}</button></>}>
        <p className="v2c-remove-text">{statusTo && STATUS[statusTo].desc} Existing invoices and commissions are not affected.</p>
      </Sheet>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMMISSIONS
// ══════════════════════════════════════════════════════════════════════════════
export function V2Commissions({ showAlert }) {
  usePageTitle("Commissions");
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("sales", "write");
  const [sum, reloadSum] = useSummary();
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("pending");
  const [who, setWho] = useState("all");
  const [sel, setSel] = useState([]);
  const [paying, setPaying] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/api/commission/all`, { headers: auth() }); const d = await r.json(); setRows(Array.isArray(d) ? d.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel([]); }, [tab, who]);

  const all = rows || [];
  const people = [...new Map(all.filter((c) => c.salesperson).map((c) => [c.salesperson._id, c.salesperson])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const shown = all.filter((c) => (tab === "all" || c.paymentStatus === tab) && (who === "all" || c.salesperson?._id === who));
  const selectable = shown.filter((c) => c.paymentStatus === "pending");
  const selTotal = all.filter((c) => sel.includes(c._id)).reduce((a, c) => a + c.commissionAmount, 0);
  const allOn = selectable.length > 0 && selectable.every((c) => sel.includes(c._id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const pay = async () => {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/commission/markpaid-bulk`, { method: "PUT", headers: json(), body: JSON.stringify({ commissionIds: sel, notes }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(apiErr(d, "Couldn't mark paid"));
      showAlert?.(`${d.updated ?? sel.length} commission${sel.length === 1 ? "" : "s"} marked paid`, "success");
      setPaying(false); setSel([]); setNotes(""); load(); reloadSum();
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <div className="v2p">
      <PageHead eyebrow="Sales team" title="Commissions" sub="What your salespeople have earned, and what is still owed." />
      <div className="v2k-stats">
        <Stat label="Pending payout" value={sum ? inr(sum.pendingCommission || 0) : "—"} note={sum ? `${sum.pendingCount || 0} records` : ""} />
        <Stat label="Paid out" value={sum ? inr(sum.paidCommission || 0) : "—"} note={sum ? `${sum.paidCount || 0} records` : ""} />
        <Stat label="Total earned" value={sum ? inr(sum.totalCommission || 0) : "—"} note={sum ? `${sum.totalCount || 0} invoices` : ""} />
        <Stat label="Salespeople earning" value={rows ? people.length : "—"} note={rows && people.length ? people.slice(0, 2).map((p) => p.name.split(" ")[0]).join(", ") + (people.length > 2 ? "…" : "") : ""} />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Payment status" value={tab} onChange={setTab} options={[{ value: "pending", label: "Pending" }, { value: "paid", label: "Paid" }, { value: "all", label: "All" }]} />
        {people.length > 1 && <div className="v2i-presets">{[{ _id: "all", name: "Everyone" }, ...people].map((p) => (
          <button key={p._id} type="button" className={`v2-chip-btn v2i-preset${who === p._id ? " is-on" : ""}`} onClick={() => setWho(p._id)}>{p.name}</button>))}</div>}
      </div>
      <section className="v2-card v2i-card">
        {!rows ? <div className="v2-list">{[0, 1, 2].map((i) => <Skel key={i} h={60} r={16} />)}</div>
          : shown.length === 0 ? <div className="v2c-empty"><Duo name="cash" size={48} /><strong>{tab === "pending" ? "Nothing to pay" : "No commissions"}</strong><span>{tab === "pending" ? "Every commission is settled." : "No commission records exist yet."}</span></div>
          : <>
            {canWrite && selectable.length > 0 && (
              <label className="v2cm-all"><input type="checkbox" className="v2-check" checked={allOn} onChange={() => setSel(allOn ? [] : selectable.map((c) => c._id))} /> Select all {selectable.length} pending</label>)}
            <div className="v2i-rows">{shown.map((c) => {
              const pending = c.paymentStatus === "pending"; const on = sel.includes(c._id);
              return (
                <label key={c._id} className={`v2cn-row v2cm-row${on ? " is-on" : ""}${!(pending && canWrite) ? " is-static" : ""}`}>
                  {canWrite && <input type="checkbox" className="v2-check" disabled={!pending} checked={on} onChange={() => toggle(c._id)} aria-label={`Select ${c.invoiceNumber}`} />}
                  <span className="v2-avatar v2-avatar--client" style={{ width: 38, height: 38, fontSize: 13 }}>{ini(c.salesperson?.name)}</span>
                  <span className="v2-row-main"><span className="v2-row-title">{c.salesperson?.name || "Removed salesperson"}</span>
                    <span className="v2-row-sub">{c.invoiceNumber || c.invoice?.invoiceNumber} · {inr(c.saleAmount)} sale · {c.commissionType === "percentage" ? `${c.commissionRate}%` : c.commissionType === "flat" ? "flat" : "—"} · {fmtDate(c.createdAt)}</span></span>
                  <span className="v2cn-end"><span className="v2l-amt">{inr(c.commissionAmount, { decimals: 2 })}</span>
                    {pending ? <span className="v2-status v2-status--warn">Pending</span> : <span className="v2-status v2-status--good">Paid {fmtDate(c.paidDate)}</span>}</span>
                </label>);
            })}</div>
          </>}
      </section>
      <div className={`v2cm-bar v2-glass${sel.length ? " is-on" : ""}`} aria-hidden={!sel.length}>
        <span><b>{sel.length}</b> selected · <Money value={selTotal} /></span>
        <button type="button" className="v2-btn v2-btn--sm" tabIndex={sel.length ? 0 : -1} onClick={() => setSel([])}>Clear</button>
        <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" tabIndex={sel.length ? 0 : -1} onClick={() => { setErr(""); setPaying(true); }}>Mark paid</button>
      </div>
      <Sheet open={paying} onClose={() => setPaying(false)} width={460} title={`Mark ${sel.length} commission${sel.length === 1 ? "" : "s"} paid?`} subtitle={inr(selTotal, { decimals: 2 })}
        footer={<><button type="button" className="v2-btn" onClick={() => setPaying(false)}>Cancel</button><button type="button" className="v2-btn v2-btn--primary" disabled={busy} onClick={pay}>{busy ? "Saving…" : `Mark ${inr(selTotal)} paid`}</button></>}>
        <div className="v2-form">
          <Field label="Note" optional hint="e.g. UTR or cheque number — saved on each record"><input className="v2-input" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          <p className="v2c-remove-text">Records today as the payment date. This can't be undone from the app.</p>
          {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
        </div>
      </Sheet>
    </div>
  );
}
